import {
  GetFormattedMoney,
  getAdditionalFeesConfig,
  getTaxConfig,
  getTaxRateLabel,
  getTicketServiceFee,
  getTicketTax,
  hasPricingOptions,
  isDonationTicketDefinition,
  isFreeTicketDefinition,
  isTaxAdded,
} from '@wix/wix-events-commons-statics'
import type {TFunction} from 'react-i18next'
import {isWixFeeTypeIsAdded} from './tickets'

interface TaxProps {
  event: ExtendedEvent
  ticket: wix.events.ticketing.TicketDefinition
  t: TFunction
  overridePrice?: string
  abstract?: boolean
  getFormattedMoney: GetFormattedMoney
}

export const getTax = ({event, ticket, t, overridePrice, abstract = false, getFormattedMoney}: TaxProps) => {
  const taxConfig = getTaxConfig(event) as wix.events.TaxConfig
  const isDonation = isDonationTicketDefinition(ticket)
  const visible = Boolean(taxConfig) && !isFreeTicketDefinition(ticket) && (!isDonation || taxConfig.appliesToDonations)

  if (!visible) {
    return null
  }

  if (!isTaxAdded(taxConfig.type)) {
    return t('tax.included', taxConfig)
  } else if (abstract) {
    return `+${getTaxRateLabel(taxConfig)} ${taxConfig.name}`
  } else {
    return `+${getTicketTax(ticket, taxConfig, overridePrice, getFormattedMoney)} ${taxConfig.name}`
  }
}

export const getFee = ({event, ticket, t, overridePrice, abstract = false, getFormattedMoney}: TaxProps) => {
  const taxConfig = getTaxConfig(event) as wix.events.TaxConfig

  if (!isWixFeeTypeIsAdded(ticket) || isFreeTicketDefinition(ticket)) {
    return null
  }

  if (abstract) {
    return `+${t('checkout_serviceFee_v2')}`
  } else {
    return t('checkout_plusServiceFee_v2', {
      fee: getTicketServiceFee(ticket, taxConfig, overridePrice, getFormattedMoney),
    })
  }
}

const hasAdditionalFees = (event: ExtendedEvent, ticket: wix.events.ticketing.TicketDefinition) =>
  getAdditionalFeesConfig(event).length > 0 && !isFreeTicketDefinition(ticket)

export const getAdditionalFeesNote = ({
  event,
  ticket,
  t,
}: {
  event: ExtendedEvent
  ticket: wix.events.ticketing.TicketDefinition
  t: TFunction
}) => (hasAdditionalFees(event, ticket) ? t('tickets_additionalFeesApply') : null)

// Pricing-option subtitles are one line, so the note joins the fee text there.
export const withAdditionalFeesNote = (
  fees: string,
  {event, ticket, t}: {event: ExtendedEvent; ticket: wix.events.ticketing.TicketDefinition; t: TFunction},
) => {
  if (!hasAdditionalFees(event, ticket)) {
    return fees
  }
  return fees ? `${fees}, ${t('tickets_plusAdditionalFees')}` : t('tickets_additionalFeesApply')
}

export const getFees = ({
  event,
  ticket,
  t,
  getFormattedMoney,
  overridePrice,
}: {
  event: ExtendedEvent
  ticket: wix.events.ticketing.TicketDefinition
  t: TFunction
  getFormattedMoney: GetFormattedMoney
  overridePrice?: string
}) => {
  const lacksPriceOverride = (isDonationTicketDefinition(ticket) || hasPricingOptions(ticket)) && !overridePrice

  if (lacksPriceOverride) {
    return getAbstractFees({event, ticket, t, getFormattedMoney})
  }

  return [
    getTax({event, ticket, t, overridePrice, getFormattedMoney}),
    getFee({event, ticket, t, overridePrice, getFormattedMoney}),
  ]
    .filter(Boolean)
    .join(', ')
}

export const getAbstractFees = ({
  event,
  ticket,
  t,
  getFormattedMoney,
}: {
  event: ExtendedEvent
  ticket: wix.events.ticketing.TicketDefinition
  t: TFunction
  getFormattedMoney: GetFormattedMoney
}) =>
  [
    getTax({event, ticket, t, abstract: true, getFormattedMoney}),
    getFee({event, ticket, t, abstract: true, getFormattedMoney}),
  ]
    .filter(Boolean)
    .join(', ')
